import { createSelector } from "@reduxjs/toolkit";

/**
 * READS OVER THE WORKSPACE SERVER CACHE.
 *
 * Every selector reads the CURRENT key only, so a view can never paint an
 * entry that belongs to a previous context. Each one tolerates a store with no
 * `workspace` key at all, which is what WorkStep's own regression harness
 * mounts.
 */

/** The task automatic traversal budget per entry (plan section 3, row B). */
export const TASK_AUTO_PAGE_BUDGET = 4;
export const TASK_PAGE_LIMIT = 50;

const COMPLETE_STATUS_ID = 5;

export const isTaskComplete = (task) =>
  Number(task?.status_id) === COMPLETE_STATUS_ID;

export const selectCurrentWorkspaceEntry = (state) => {
  const slice = state?.workspace;
  if (!slice?.current?.contextKey) return null;
  return slice.byKey?.[slice.current.contextKey] ?? null;
};

export const selectCurrentWorkspaceKey = (state) =>
  state?.workspace?.current?.contextKey ?? null;

/**
 * `probing` whenever there is no current entry: no ready context, or no
 * workspace reducer at all.
 */
export const selectWorkspaceStatus = (state) =>
  selectCurrentWorkspaceEntry(state)?.status ?? "probing";

/**
 * THE GOOGLE PROJECTION FOR THE CURRENT KEY (FE2 plan 4.3), or null.
 *
 * Null unless the workspace is enabled AND the server said
 * `capabilities.google === true`. Null is the flag-off state: no Google UI
 * renders and no Google request is made. A hint only; the server re-checks
 * every mutation.
 */
export const selectWorkspaceGoogle = (state) => {
  const entry = selectCurrentWorkspaceEntry(state);
  if (!entry || entry.status !== "enabled") return null;
  if (entry.capabilities?.google !== true) return null;
  return entry.google ?? null;
};

const orderedItems = (collection) =>
  (collection?.order ?? []).map((id) => collection.byId[id]).filter(Boolean);

export const selectWorkspaceTasks = createSelector(
  [selectCurrentWorkspaceEntry],
  (entry) => orderedItems(entry?.tasks),
);

const selectActorId = (state) => state?.user?.user?.id ?? null;

export const selectOwnWorkspaceTasks = createSelector(
  [selectWorkspaceTasks, selectActorId],
  (tasks, actorId) =>
    actorId === null
      ? []
      : tasks.filter((task) => Number(task.user_id) === Number(actorId)),
);

/**
 * THE DEFAULT SELECTION (decision D-3). A starting position only: the first
 * incomplete own task by due date then id, else the first own task. It is not
 * a recommendation and nothing renders it as one.
 */
export const defaultTaskId = (ownTasks) => {
  if (!ownTasks.length) return null;
  const dueOf = (task) => {
    const at = task.due_date ? Date.parse(task.due_date) : NaN;
    return Number.isNaN(at) ? Number.POSITIVE_INFINITY : at;
  };
  const byDue = [...ownTasks].sort(
    (a, b) => dueOf(a) - dueOf(b) || Number(a.id) - Number(b.id),
  );
  const firstOpen = byDue.find((task) => !isTaskComplete(task));
  return Number((firstOpen ?? byDue[0]).id);
};

const unique = (values) => [...new Set(values)];

const outputBoundFileIds = (tasks, outputs) => {
  const outputIds = new Set(outputs.map((o) => Number(o.milestone_id)));
  return unique(
    tasks
      .filter((task) => outputIds.has(Number(task.milestone_id)))
      .flatMap((task) => (task.file_ids ?? []).map(Number)),
  );
};

/**
 * Linked file ids the cache does not hold yet, minus the ones whose
 * hydration already failed (those wait for an explicit Retry). Every task's
 * links count, not only output-bound ones, because the rail lists the
 * selected task's files whether or not the task feeds an output.
 */
/* ===================== LEVEL REFERENCES (Slice R) =====================
   A reference row is `available` or `unavailable`. An unavailable row carries
   no source metadata at all. An available row is branched on `source_type`,
   never on whether `file` is present: a submission row serializes
   `file: null` (addendum 1, note 2). */

export const isAvailableFileReference = (reference) =>
  reference?.state === "available" && reference?.source_type === "file";

/** The newest available file reference for a source file id, or null. */
export const fileReferenceFor = (entry, fileId) => {
  if (!entry) return null;
  const key = Number(fileId);
  for (const id of entry.references.order) {
    const reference = entry.references.byId[id];
    if (
      isAvailableFileReference(reference) &&
      Number(reference.source_id) === key
    ) {
      return reference;
    }
  }
  return null;
};

/**
 * A file shown at this level: a direct working file, else the nested file
 * of an available file reference (an earlier file reused at this level and
 * possibly linked to a task here, revision-3 closure line 107).
 */
export const resolveLevelFile = (entry, fileId) => {
  if (!entry) return null;
  const key = Number(fileId);
  if (entry.files.byId[key]) return entry.files.byId[key];
  const reference = fileReferenceFor(entry, key);
  return reference?.file ? { ...reference.file, id: key } : null;
};

/**
 * `source_level_number` 0 means the level is unknown: the label is "Earlier
 * work", never "Level 0" (addendum 1, note 3).
 */
export const referenceLevelNumber = (reference) => {
  const n = Number(reference?.source_level_number);
  return Number.isInteger(n) && n > 0 ? n : null;
};

export const selectMissingLinkedFileIds = createSelector(
  [selectCurrentWorkspaceEntry, selectWorkspaceTasks],
  (entry, tasks) => {
    if (!entry) return [];
    /* An id an available file reference already provides is not missing:
       re-reading it in THIS capsule would find nothing and strip the link. */
    return unique(
      tasks.flatMap((task) => (task.file_ids ?? []).map(Number)),
    ).filter(
      (id) => !resolveLevelFile(entry, id) && !entry.files.hydrationErrors[id],
    );
  },
);

const hasInflightTaskPage = (entry) =>
  Object.keys(entry.inflight).some((key) => key.startsWith("page:tasks:"));

/**
 * OUTPUT GROUPS (plan section 3, row B). No canonical document: each output
 * is its title with the de-duplicated union of the files its tasks link.
 *
 * `state` is:
 *   `complete`  every task page is loaded AND every linked file is cached
 *   `loading`   still traversing or hydrating inside the budget
 *   `partial`   the budget ran out (`more_tasks`) or a read failed
 *               (`load_failed`); the group shows what it has, never a silently
 *               smaller set and never an endless loading tile.
 */
export const selectOutputGroups = createSelector(
  [selectCurrentWorkspaceEntry, selectWorkspaceTasks],
  (entry, tasks) => {
    if (!entry) return [];
    const budgetSpent =
      !entry.tasks.complete &&
      entry.tasks.autoFollowed >= TASK_AUTO_PAGE_BUDGET &&
      !hasInflightTaskPage(entry);
    return entry.outputs.map((output) => {
      const milestoneId = Number(output.milestone_id);
      const groupTasks = tasks.filter(
        (task) => Number(task.milestone_id) === milestoneId,
      );
      const fileIds = unique(
        groupTasks.flatMap((task) => (task.file_ids ?? []).map(Number)),
      ).sort((a, b) => b - a);
      const files = fileIds
        .map((id) => resolveLevelFile(entry, id))
        .filter(Boolean);
      const missing = fileIds.filter((id) => !resolveLevelFile(entry, id));
      const failed = missing.filter((id) => entry.files.hydrationErrors[id]);

      let state = "loading";
      let partialReason = null;
      if (entry.tasks.complete && missing.length === 0) {
        state = "complete";
      } else if (entry.tasks.error || failed.length > 0) {
        state = "partial";
        partialReason = "load_failed";
      } else if (budgetSpent) {
        state = "partial";
        partialReason = "more_tasks";
      }
      return {
        milestoneId,
        title: output.title,
        tasks: groupTasks,
        files,
        state,
        partialReason,
      };
    });
  },
);

/** Files linked to no output-bound task, in list order. */
export const selectSquadFiles = createSelector(
  [selectCurrentWorkspaceEntry, selectWorkspaceTasks],
  (entry, tasks) => {
    if (!entry) return [];
    const linked = new Set(outputBoundFileIds(tasks, entry.outputs));
    return orderedItems(entry.files).filter((file) => !linked.has(file.id));
  },
);

/** The session id under which a reference is opened. */
export const referenceDocId = (referenceId) => `ref:${Number(referenceId)}`;

const referenceTarget = (reference) => ({
  file: reference.file
    ? { ...reference.file, id: Number(reference.source_id) }
    : null,
  capsuleId: Number(reference.source_capsule_id) || null,
  /* Pinned (> 0) sends that revision; unpinned (0) omits it and the latest
     validated revision applies (addendum 1, note 1). */
  revisionId:
    Number(reference.source_version) > 0
      ? Number(reference.source_version)
      : null,
  levelNumber: referenceLevelNumber(reference),
  reference,
});

/**
 * What an open document id points at.
 *
 *   `ref:<id>`   an available file reference: its nested file, its SOURCE
 *                capsule and its pinned revision (or none)
 *   a number     a current-level file, else a loaded earlier-level file, else
 *                an available file reference to that source file (a related
 *                file linked through a reference)
 */
export const findWorkspaceFile = (entry, id) => {
  if (!entry || id === null || id === undefined) return null;
  if (typeof id === "string" && id.startsWith("ref:")) {
    const reference = entry.references.byId[Number(id.slice(4))];
    return isAvailableFileReference(reference)
      ? referenceTarget(reference)
      : null;
  }
  const key = Number(id);
  if (entry.files.byId[key])
    return { file: entry.files.byId[key], capsuleId: null, revisionId: null };
  for (const [capsuleId, level] of Object.entries(entry.earlier)) {
    if (level?.byId?.[key]) {
      return {
        file: level.byId[key],
        capsuleId: Number(capsuleId),
        revisionId: null,
      };
    }
  }
  const reference = fileReferenceFor(entry, key);
  return reference ? referenceTarget(reference) : null;
};

/**
 * THE EARLIER-WORK ROWS (revision-3 closure, line 118). References render
 * beside the level's files, de-duplicated by canonical source file id: a
 * file reference whose source file is already shown as a related document of
 * an output, or by an earlier row, is not repeated. Unavailable rows and
 * submission rows are kept as they are.
 */
export const selectReferenceRows = createSelector(
  [selectCurrentWorkspaceEntry, selectWorkspaceTasks],
  (entry, tasks) => {
    if (!entry) return [];
    const shown = new Set(outputBoundFileIds(tasks, entry.outputs));
    const rows = [];
    entry.references.order.forEach((id) => {
      const reference = entry.references.byId[id];
      if (!reference) return;
      if (reference.state !== "available") {
        rows.push({ kind: "unavailable", id: Number(id) });
        return;
      }
      if (reference.source_type === "file") {
        const sourceId = Number(reference.source_id);
        if (shown.has(sourceId)) return;
        shown.add(sourceId);
        rows.push({ kind: "file", id: Number(id), reference });
        return;
      }
      if (reference.source_type === "submission") {
        rows.push({ kind: "submission", id: Number(id), reference });
      }
    });
    return rows;
  },
);
